import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { useARC } from "./ARC/ARCProvider";
import { useFeatures } from "./FeaturesProvider";

import { ConnectionState } from "lib/consts/ARC/connection";
import {
    DefaultEnableArmControl,
    DefaultArmStickMode,
    ARM_STICK_MODE_TRANSLATE,
    ARM_STICK_MODE_ROTATE,
    armStatesInfo,
} from "lib/consts/arm";
import {
    attachRosTopicWithRetry,
    LATE_ROS_TOPIC_OPTIONS,
} from "lib/ros/rosTopicSubscribeWithRetry";
import {
    setArmCmdVel,
    zeroArmCmdVel,
    setArmPresetArg,
    armStop,
    armGoZero,
    armGripperOpen,
    armGripperClose,
    armPresetStow,
    armPresetReady,
    armSaveStow,
    armSavePresetTrigger,
    armGotoPresetTrigger,
    armDeletePresetTrigger,
} from "lib/protocols/arm";

export { ARM_STICK_MODE_TRANSLATE, ARM_STICK_MODE_ROTATE };

export const ArmControlContext = createContext(null);
ArmControlContext.displayName = "ArmControl";

export const useArmControl = () => {
    const context = useContext(ArmControlContext);
    if (!context) {
        throw new Error("useArmControl must be used within ArmControlProvider");
    }
    return context;
};

/** Allow /arm/preset_arg to reach the bridge before the Trigger call. */
const PRESET_ARG_SETTLE_MS = 120;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ArmControlProvider = ({ children }) => {
    const { controlSocket, connectionState, sendCommand: send, callService } = useARC();
    const { hasArm } = useFeatures();

    const [armState, setArmState] = useState(armStatesInfo.state.default);
    const [controlArm, setControlArm] = useState(DefaultEnableArmControl);
    const [armStickMode, setArmStickMode] = useState(DefaultArmStickMode);

    const sendArmCmdVel = useCallback(
        (twist) => send(setArmCmdVel(twist)),
        [send]
    );

    const sendArmCmdVelZero = useCallback(() => send(zeroArmCmdVel()), [send]);

    const setArmStickModeSafe = useCallback(
        (mode) => {
            setArmStickMode(mode);
            sendArmCmdVelZero();
        },
        [sendArmCmdVelZero]
    );

    const callArm = useCallback(
        (protocolFn) => callService(...protocolFn()),
        [callService]
    );

    /** Publish preset name, wait briefly, then call the matching Trigger service. */
    const callNamedPreset = useCallback(
        async (name, triggerFn) => {
            const trimmed = String(name ?? "").trim();
            if (!trimmed) {
                throw new Error("preset name required");
            }
            // Built-in shortcuts — no latched arg needed.
            if (triggerFn === armGotoPresetTrigger) {
                if (trimmed === "stow") return callArm(armPresetStow);
                if (trimmed === "ready") return callArm(armPresetReady);
                if (trimmed === "go_zero") return callArm(armGoZero);
            }
            if (triggerFn === armSavePresetTrigger && trimmed === "stow") {
                return callArm(armSaveStow);
            }
            send(setArmPresetArg(trimmed));
            await sleep(PRESET_ARG_SETTLE_MS);
            return callService(...triggerFn());
        },
        [callArm, callService, send]
    );

    const stop = useCallback(() => callArm(armStop), [callArm]);
    const goZero = useCallback(() => callArm(armGoZero), [callArm]);
    const gripperOpen = useCallback(() => callArm(armGripperOpen), [callArm]);
    const gripperClose = useCallback(() => callArm(armGripperClose), [callArm]);
    const presetStow = useCallback(() => callArm(armPresetStow), [callArm]);
    const presetReady = useCallback(() => callArm(armPresetReady), [callArm]);
    const saveStow = useCallback(() => callArm(armSaveStow), [callArm]);
    const savePreset = useCallback(
        (name) => callNamedPreset(name, armSavePresetTrigger),
        [callNamedPreset]
    );
    const gotoPreset = useCallback(
        (name) => callNamedPreset(name, armGotoPresetTrigger),
        [callNamedPreset]
    );
    const deletePreset = useCallback(
        (name) => callNamedPreset(name, armDeletePresetTrigger),
        [callNamedPreset]
    );

    const presets = armState?.presets && typeof armState.presets === "object"
        ? armState.presets
        : {};
    const presetNames = Object.keys(presets).sort((a, b) => a.localeCompare(b));

    useEffect(() => {
        if (!hasArm && controlArm) {
            setControlArm(false);
            sendArmCmdVelZero();
        }
    }, [hasArm, controlArm, sendArmCmdVelZero]);

    useEffect(() => {
        if (!controlSocket || connectionState !== ConnectionState.Connected || !hasArm) {
            setArmState(null);
            return;
        }

        const info = armStatesInfo.state;
        const cleanup = attachRosTopicWithRetry(
            controlSocket,
            info.topic,
            (data) => setArmState(info.transform ? info.transform(data) : data),
            { ...LATE_ROS_TOPIC_OPTIONS }
        );

        return () => cleanup();
    }, [controlSocket, connectionState, hasArm]);

    return (
        <ArmControlContext.Provider
            value={{
                controlArm,
                setControlArm,
                armStickMode,
                setArmStickMode: setArmStickModeSafe,
                armState,
                sendArmCmdVel,
                sendArmCmdVelZero,
                stop,
                goZero,
                gripperOpen,
                gripperClose,
                presetStow,
                presetReady,
                saveStow,
                savePreset,
                gotoPreset,
                deletePreset,
                presets,
                presetNames,
            }}
        >
            {children}
        </ArmControlContext.Provider>
    );
};

export default ArmControlProvider;
